import { api } from "@/lib/api";

export const newKey = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

async function retry(fn, tries = 3) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      const status = err.response?.status;
      if (attempt >= tries - 1 || (status && status < 500 && status !== 429)) throw err;
      await wait(1500 * (attempt + 1));
    }
  }
}

export async function startJson(text, source, filename) {
  const { data } = await api.post("/imports/json", { text, source, filename, request_key: newKey() });
  return data;
}

export async function sendPackage(file, onProgress) {
  const { data: up } = await api.post("/imports/uploads", { filename: file.name, size: file.size });
  for (let i = 0; i < up.total_chunks; i += 1) {
    const blob = file.slice(i * up.chunk_size, (i + 1) * up.chunk_size);
    await retry(() => api.put(`/imports/uploads/${up.upload_id}/chunks/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } }));
    onProgress?.(i + 1, up.total_chunks);
  }
  await retry(() => api.post(`/imports/uploads/${up.upload_id}/complete`));
  return up.upload_id;
}

export async function processPackage(draftId, onProgress, stopped) {
  for (;;) {
    const { data } = await retry(() => api.post(`/imports/${draftId}/package/process?limit=8`));
    onProgress?.(data.draft);
    const status = data.draft.package?.status;
    if (!["pending", "processing"].includes(status) || stopped?.()) return data.draft;
    if (data.busy) await wait(1500);
  }
}

export async function finalizeAll(draftId, partial, onProgress, stopped) {
  let created = 0;
  let failed = [];
  for (;;) {
    const { data } = await retry(() => api.post(`/imports/${draftId}/finalize`, { partial, limit: 20 }));
    created += data.results.filter((r) => r.ok).length;
    failed = failed.concat(data.results.filter((r) => !r.ok));
    onProgress?.(data.draft, created);
    if (!data.remaining_ready || data.finished || !data.results.length || stopped?.()) return { ...data, created, failed };
  }
}

export async function uploadToDraft(draftId, files, onProgress) {
  const results = [];
  for (let i = 0; i < files.length; i += 10) {
    const form = new FormData();
    files.slice(i, i + 10).forEach((f) => form.append("files", f));
    const { data } = await retry(() => api.post(`/imports/${draftId}/images`, form));
    results.push(...data.files);
    onProgress?.(Math.min(i + 10, files.length), files.length);
  }
  return results;
}

export async function download(path, params, filename) {
  const { data } = await api.get(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const STATES = {
  ready: { label: "Ready", cls: "border-emerald-600/50 text-emerald-300" },
  missing_images: { label: "Needs images", cls: "border-amber-500/60 text-amber-300" },
  error: { label: "Needs fixes", cls: "border-red-600/60 text-red-300" },
  skip: { label: "Will skip", cls: "border-slate-600 text-slate-400" },
  skipped: { label: "Skipped", cls: "border-slate-600 text-slate-400" },
  finalized: { label: "Imported", cls: "border-aether-400/60 text-aether-300" },
};

export const faceLabel = (item, face) => (item.faces_required > 1 ? (face === 0 ? "Front" : "Back") : "Card image");
export const itemName = (item) => (item.card?.faces || []).map((f) => f.name).filter(Boolean).join(" // ") || item.import_id || "Unnamed card";
